/** Display formatting helpers. Everything tolerates null so callers can pass DTO fields directly. */

const EM_DASH = '—';

export function formatMoney(
  value: number | null | undefined,
  currencyCode?: string | null,
  fallback = EM_DASH,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  const code = (currencyCode ?? '').trim().toUpperCase();
  try {
    if (code.length === 3) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
  } catch {
    // Unknown currency code: fall through to a plain number with the code appended.
  }
  const plain = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return code.length > 0 ? `${plain} ${code}` : plain;
}

/** Compact money for stat tiles, e.g. 1.2M. */
export function formatMoneyCompact(
  value: number | null | undefined,
  currencyCode?: string | null,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  const code = (currencyCode ?? '').trim().toUpperCase();
  const abs = Math.abs(value);
  if (abs < 100_000) return formatMoney(value, currencyCode);
  try {
    return new Intl.NumberFormat(undefined, {
      style: code.length === 3 ? 'currency' : 'decimal',
      currency: code.length === 3 ? code : undefined,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return formatMoney(value, currencyCode);
  }
}

export function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Confidence values arrive either as 0-1 fractions or 0-100 percentages depending on the source
 * (bot readiness scores are 0-100, vendor match confidence is 0-1). Normalise to 0-100.
 */
export function toPercent(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return value <= 1 && value >= -1 ? value * 100 : value;
}

export function formatPercent(value: number | null | undefined, fractionDigits = 0): string {
  const pct = toPercent(value);
  if (pct === null) return EM_DASH;
  return `${pct.toFixed(fractionDigits)}%`;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  // "yyyy-MM-dd" (C# DateOnly) is parsed as UTC midnight by Date; render it as a plain calendar
  // date to avoid a timezone-induced off-by-one day.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(value);
  if (dateOnly) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value: string | null | undefined, fallback = EM_DASH): string {
  const date = parseDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
}

export function formatDateTime(value: string | null | undefined, fallback = EM_DASH): string {
  const date = parseDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatShortDay(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return EM_DASH;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

/** "3 minutes ago" / "in 4 days". */
export function formatRelative(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return EM_DASH;
  const deltaSeconds = (date.getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [unit, seconds] of units) {
    if (Math.abs(deltaSeconds) >= seconds || unit === 'second') {
      return formatter.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return EM_DASH;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return EM_DASH;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

export function formatDuration(milliseconds: number | null | undefined): string {
  if (milliseconds === null || milliseconds === undefined || Number.isNaN(milliseconds)) {
    return EM_DASH;
  }
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  return `${(milliseconds / 1000).toFixed(2)} s`;
}

/** ISO date string for an <input type="date">, or '' when absent. */
export function toDateInputValue(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Turns an <input type="date"> value back into something the API will bind to DateTime?. */
export function fromDateInputValue(value: string): string | null {
  if (!value) return null;
  return `${value}T00:00:00`;
}

export function parseDecimalInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function titleCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
